/**
 * Wear and repair rules, shared so the client can show an item's condition and what mending it
 * takes, exactly as the server reckons it. Quality and wear live on the item id
 * ("iron_sword+fine+worn"); the use count toward the next stage is `character.wear[id]`.
 */
import { ITEMS, type ItemId } from './content/items.js';
import { GEAR, SMITHED, isGearId, parseVariant, variantId, type Grade } from './content/equipment.js';
import { TOOLS, isToolId } from './content/tools.js';
import { RECIPE_FOR } from './content/crafting.js';

const GRADE_DURABILITY: Record<Grade, number> = { fine: 1.15, superior: 1.3, master: 1.5 };

/** Uses before an item goes from fresh to worn (and again from worn to broken). Null: it never wears. */
export function durability(id: string): number | null {
  const d = isGearId(id) ? GEAR[id] : isToolId(id) ? TOOLS[id] : null;
  if (!d || !d.metal || !SMITHED[d.metal as keyof typeof SMITHED] || (isGearId(id) && GEAR[id].slot === 'ammo')) return null;
  const { grade } = parseVariant(id);
  return Math.round(SMITHED[d.metal as keyof typeof SMITHED]!.durability * (grade ? GRADE_DURABILITY[grade] : 1));
}

/** 0..1: how much life is left before the next stage (1 is fresh), or null if it never wears. */
export function condition(id: string, uses: number | undefined): number | null {
  const max = durability(id);
  if (!max) return null;
  if (parseVariant(id).wear === 'broken') return 0;
  return Math.max(0, 1 - (uses ?? 0) / max);
}

/** The same item, mended. */
export const repairedId = (id: string) => { const v = parseVariant(id); return variantId(v.base, v.grade, null) as ItemId; };

/** The bar a smithed item is made from, and the Smithing level to mend it yourself. */
export function repairNeeds(id: string): { bar: ItemId; level: number; xp: number } | null {
  const { base, wear } = parseVariant(id);
  if (!wear) return null;
  const r = RECIPE_FOR[base];
  if (!r) return null;
  const bar = Object.keys(r.inputs).find(k => k in ITEMS && ITEMS[k as ItemId].cat === 'bar') as ItemId | undefined;
  return bar ? { bar, level: Math.max(1, r.level - 5), xp: Math.round(r.xp * 0.25) } : null;
}

/** Coins Bram asks to mend it. */
export const repairCost = (id: string) => { const v = parseVariant(id), base = ITEMS[repairedId(id)]?.value ?? 10; return Math.max(5, Math.round(base * (v.wear === 'broken' ? 0.6 : 0.25))); };
